/**
 * fieldLabels: the one label helper for a registry token. Every surface
 * that derives a display label from a token (RowEditDialog, ConfirmQueue,
 * cardSpine) routes through here.
 *
 * A word of a token that is an abbreviation reads in its standard form;
 * the abbreviation list is the one dictionary in @/lib/valueCase (POA,
 * HCP, POLST, DNR, ER, LTC and the rest), so labels and record values
 * never disagree on a word.
 *
 *   humanizeToken('rehab_facility') -> 'Rehab facility'
 *   humanizeToken('polst')          -> 'POLST'
 *   humanizeToken('er_visit')       -> 'ER visit'
 *   humanizeToken('egfr_result')    -> 'eGFR result'
 *   humanizeToken('medicare_a')     -> 'Medicare A'
 *   fieldLabel('is_poa')            -> 'Is POA'
 *   fieldLabel('is_billing')        -> 'Is billing contact'
 */

import { abbreviationFor } from '@/lib/valueCase';
import { pronounForms } from '@/lib/pronounForms';
import type { ClientPronouns } from '@/lib/clientRecord';

/**
 * Whole-token wording overrides — labels a per-word humanize can't derive
 * (WS-8 caps sweep, founder verbatim 2026-07-10):
 *   - Medicare program parts carry a trailing capital letter ("Medicare A",
 *     never "Medicare a") and "Medicare Advantage" is a product name;
 *   - DPOA reads with its plain-words expansion wherever it labels a row
 *     (founder: clarify what it stands for);
 *   - coverage_change is a wire token (NEVER renamed on the wire) whose
 *     display label follows the coverage -> insurance rename.
 */
const TOKEN_LABEL_OVERRIDES: Record<string, string> = {
  medicare_a: 'Medicare A',
  medicare_b: 'Medicare B',
  medicare_a_b: 'Medicare A + B',
  medicare_advantage: 'Medicare Advantage',
  part_d: 'Part D',
  dpoa: 'DPOA (durable power of attorney)',
  coverage_change: 'Insurance change',
  // Design Pass 2 (A3, state-term law): the straight humanize reads
  // "Appointment past", which parrots the wire token instead of English
  // and collides with the record's WS-5 "Past appointment" rename — one
  // rendering per state term. Display layer only; wire tokens unchanged.
  appointment_past: 'Past appointment',
  appointment_upcoming: 'Upcoming appointment',
  // The stored recurrence token reads in words wherever it shows: the
  // record dialog's choice, the confirm card's choice and its fact.
  biweekly: 'Every two weeks',
  // The risk category, wherever it shows: the risk card, the record
  // header's risk pill and the record dialog.
  financial_legal: 'Financial or legal',
  // The proposal type on the confirm card's chip.
  provider_new: 'New care provider',
};

/** Field-key wording overrides (label ≠ a straight humanize). */
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  // WS-4 D4: the checkbox reads "Is billing contact", not "Is billing"
  // (founder wording 2026-07-10).
  is_billing: 'Is billing contact',
};

/**
 * A contact's roles (0227's packet): one set of words, in the contact
 * form's order, for the form, the Contacts card's chips, the face sheet
 * and the confirm card. "Lives with" takes the client's pronoun
 * (contactRoleWord), "this client" when none is recorded.
 */
export const CONTACT_ROLE_KEYS = ['is_emergency', 'lives_with', 'is_poa', 'is_hcp', 'is_guardian', 'is_billing'] as const;
export type ContactRoleKey = (typeof CONTACT_ROLE_KEYS)[number];

export const CONTACT_ROLE_WORDS: Readonly<Record<ContactRoleKey, string>> = {
  is_emergency: 'Emergency contact',
  lives_with: 'Lives with this client',
  is_poa: 'Power of attorney (POA)',
  is_hcp: 'Healthcare proxy (HCP)',
  is_guardian: 'Guardian',
  is_billing: 'Billing contact',
};

/** A contact role's words; "Lives with" names the client by pronoun. */
export function contactRoleWord(key: ContactRoleKey, pronouns: ClientPronouns | null): string {
  if (key === 'lives_with') return `Lives with ${pronounForms(pronouns).object}`;
  return CONTACT_ROLE_WORDS[key];
}

function isContactRoleKey(key: string): key is ContactRoleKey {
  return (CONTACT_ROLE_KEYS as readonly string[]).includes(key);
}

/** A boolean field's checkbox-row label: a contact role's words (no
 *  pronoun in hand: "Lives with this client"), else fieldLabel. */
export function checkboxFieldLabel(key: string): string {
  return isContactRoleKey(key) ? contactRoleWord(key, null) : fieldLabel(key);
}

/** Humanize a token: '_'-split, each abbreviation in its standard form,
 *  then a first capital unless the first word was an abbreviation. */
export function humanizeToken(token: string): string {
  const override = TOKEN_LABEL_OVERRIDES[token];
  if (override) return override;
  const words = token.split('_').filter(Boolean);
  const shown = words.map((w) => abbreviationFor(w.toLowerCase(), 'token') ?? w.toLowerCase());
  const s = shown.join(' ');
  const firstReplaced = words.length > 0 && abbreviationFor(words[0]!.toLowerCase(), 'token') !== null;
  return firstReplaced ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/** A registry field key's display label (overrides, then acronym-aware). */
export function fieldLabel(key: string): string {
  return FIELD_LABEL_OVERRIDES[key] ?? humanizeToken(key);
}

/**
 * The few labels a card fact reads that no record form has: the note's
 * own words for a date (an appointment's "When", a hospital stay's
 * "Date") and a new provider's display name ("Name"). Every other fact
 * reads its record form's label (confirmQueueActions cardFieldLabel).
 * Keyed by proposal type, then field.
 */
const FACT_LABEL_OVERRIDES: Record<string, Record<string, string>> = {
  appointment_upcoming: { date_text: 'When' },
  appointment_past: { date_text: 'When' },
  hospital_event: { date_text: 'Date' },
  provider_new: { display_name: 'Name' },
};

/** A card fact's own label where no record form has one, else undefined. */
export function factLabelOverride(proposalType: string, field: string): string | undefined {
  return FACT_LABEL_OVERRIDES[proposalType]?.[field];
}
